// -----------------------------------------------------------------------------
// Message list + row.
//
// `MessageList` walks the (chronological) messages array, inserting day
// headings whenever the calendar date changes and collapsing the avatar /
// author header for consecutive messages from the same person within five
// minutes. `MessageRow` renders a Microsoft-Teams-style chat bubble: own
// messages right-aligned with a primary-coloured fill, everyone else
// left-aligned with a white card. Reactions hang off the bottom corner of
// the bubble and the inline edit / react controls float on the opposite
// side from the bubble itself.
// -----------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUturnLeftIcon,
  FaceSmileIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { fullNameFor } from "../../utils/chatHelpers";
import { IChatMessage, IChatUser } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import ChatMessageAttachments from "../../components/chat/ChatMessageAttachments";
import ChatMessageBody from "../../components/chat/ChatMessageBody";
import ChatComposerInput, {
  ChatComposerInputHandle,
} from "../../components/chat/ChatComposerInput";
import { formatDayHeading, formatTime, parseServerIso } from "./chatTime";

const REACTION_EMOJIS = ["👍", "🎉", "❤️", "😂", "🙌", "👀"];

/** DOM id for a message row - used by the reply quote and search results
 * to scroll a loaded message into view. */
export const messageDomId = (messageId: string) => `chat-msg-${messageId}`;

/** Scrolls a loaded message into view and flashes it briefly. Returns
 * false when the message isn't in the DOM (i.e. not loaded yet). */
export function scrollToMessage(messageId: string): boolean {
  const el = document.getElementById(messageDomId(messageId));
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("bg-primary-50");
  window.setTimeout(() => el.classList.remove("bg-primary-50"), 1200);
  return true;
}

interface MessageListProps {
  messages: IChatMessage[];
  usersById: Map<string, IChatUser>;
  meId: string;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
  onReply?: (message: IChatMessage) => void;
}

export default function MessageList({
  messages,
  usersById,
  meId,
  onEdit,
  onReact,
  onReply,
}: MessageListProps) {
  // Lookup for rendering reply quotes without an O(n) scan per row.
  const messagesById = useMemo(() => {
    const map = new Map<string, IChatMessage>();
    messages.forEach((m) => map.set(m.id, m));
    return map;
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-400">
        No messages yet — say hello.
      </div>
    );
  }

  type MsgRow = {
    key: string;
    message: IChatMessage;
    showHeader: boolean;
    isLastInGroup: boolean;
  };
  type DayGroup = { day: string; label: string; rows: MsgRow[] };

  // Group messages by calendar day. Each group gets its own wrapper so
  // the sticky day heading only sticks while *its* day is on screen and
  // the next day's heading pushes it out - sticky headings that are all
  // siblings of one scroll container would otherwise pile up on top of
  // each other once older history is loaded in.
  const groups: DayGroup[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    const prev = i > 0 ? messages[i - 1] : null;
    const next = i < messages.length - 1 ? messages[i + 1] : null;

    const day = parseServerIso(m.createdAt).toISODate() ?? "unknown";
    const prevDay = prev ? parseServerIso(prev.createdAt).toISODate() : null;
    const nextDay = next ? parseServerIso(next.createdAt).toISODate() : null;

    if (day !== prevDay || groups.length === 0) {
      groups.push({
        day,
        label: formatDayHeading(m.createdAt),
        rows: [],
      });
    }

    const sameAuthorAsPrev =
      prev?.authorId === m.authorId && day === prevDay;
    const within5MinPrev =
      !!prev &&
      Math.abs(Date.parse(m.createdAt) - Date.parse(prev.createdAt)) <
        5 * 60 * 1000;

    const sameAuthorAsNext =
      next?.authorId === m.authorId && day === nextDay;
    const within5MinNext =
      !!next &&
      Math.abs(Date.parse(next.createdAt) - Date.parse(m.createdAt)) <
        5 * 60 * 1000;

    groups[groups.length - 1].rows.push({
      key: m.id,
      message: m,
      showHeader: !sameAuthorAsPrev || !within5MinPrev,
      isLastInGroup: !sameAuthorAsNext || !within5MinNext,
    });
  }

  return (
    <>
      {groups.map((group) => (
        <div key={`day-${group.day}`}>
          <div className="sticky top-0 z-10 my-3 flex items-center justify-center">
            <span className="rounded-full border border-gray-200 bg-white px-3 py-0.5 text-[11px] font-medium text-gray-500 shadow-sm">
              {group.label}
            </span>
          </div>
          {group.rows.map((row) => {
            const author = usersById.get(row.message.authorId);
            return (
              <MessageRow
                key={row.key}
                message={row.message}
                author={author}
                isMine={row.message.authorId === meId}
                meId={meId}
                usersById={usersById}
                messagesById={messagesById}
                showHeader={row.showHeader}
                isLastInGroup={row.isLastInGroup}
                onEdit={onEdit}
                onReact={onReact}
                onReply={onReply}
              />
            );
          })}
        </div>
      ))}
    </>
  );
}

interface MessageRowProps {
  message: IChatMessage;
  author?: IChatUser;
  isMine: boolean;
  meId: string;
  usersById: Map<string, IChatUser>;
  messagesById: Map<string, IChatMessage>;
  showHeader: boolean;
  isLastInGroup: boolean;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
  onReply?: (message: IChatMessage) => void;
}

function MessageRow({
  message,
  author,
  isMine,
  meId,
  usersById,
  messagesById,
  showHeader,
  isLastInGroup,
  onEdit,
  onReact,
  onReply,
}: MessageRowProps) {
  const [editing, setEditing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const editRef = useRef<ChatComposerInputHandle>(null);

  useEffect(() => {
    if (editing) editRef.current?.focus();
  }, [editing]);

  const handleSave = () => {
    const next = (editRef.current?.getValue() ?? "").trim();
    if (next && next !== message.body) {
      onEdit(message.id, next);
    }
    setEditing(false);
  };

  // Bubble corner-rounding mimics MS Teams: the corner closest to the
  // author's "track" gets a tighter radius on the last message of a
  // group so the bubble has a subtle tail. Middle and first-in-group
  // messages stay fully rounded for a clean stacked look.
  const tailCorner = isMine
    ? isLastInGroup
      ? "rounded-br-sm"
      : ""
    : isLastInGroup
      ? "rounded-bl-sm"
      : "";

  const bubbleBase = classNames(
    "max-w-full whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm shadow-sm",
    tailCorner,
    isMine
      ? "bg-primary-600 text-white"
      : "border border-gray-200 bg-white text-gray-800"
  );

  const repliedTo = message.replyToId
    ? messagesById.get(message.replyToId)
    : undefined;
  const repliedAuthor = repliedTo
    ? usersById.get(repliedTo.authorId)
    : undefined;

  return (
    <div
      id={messageDomId(message.id)}
      className={classNames(
        "group relative flex gap-2.5 rounded-lg transition-colors duration-500",
        showHeader ? "mt-3" : "mt-0.5",
        isMine ? "justify-end" : ""
      )}
    >
      {/* Avatar gutter — only shown for others, only on the first
          message of a group. Subsequent messages reserve the same
          width so bubbles stay aligned. */}
      {!isMine && (
        <div className="w-9 shrink-0 pt-0.5">
          {showHeader && author ? (
            <ChatAvatar user={author} size="sm" showPresence />
          ) : null}
        </div>
      )}

      <div
        className={classNames(
          "order-2 flex min-w-0 max-w-[78%] flex-col",
          isMine ? "items-end" : "items-start"
        )}
      >
        {showHeader ? (
          <div
            className={classNames(
              "mb-0.5 flex items-baseline gap-2 px-1",
              isMine ? "flex-row-reverse" : ""
            )}
          >
            {!isMine && (
              <span className="text-sm font-semibold text-gray-900">
                {author ? fullNameFor(author) : "Unknown"}
              </span>
            )}
            {!isMine && author?.role ? (
              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                {author.role}
              </span>
            ) : null}
            <span className="text-[11px] text-gray-400">
              {formatTime(message.createdAt)}
            </span>
          </div>
        ) : null}

        {editing ? (
          <div
            className={classNames(
              "w-full rounded-2xl border border-primary-300 bg-white shadow-sm",
              tailCorner
            )}
          >
            <div className="px-3 py-2">
              <ChatComposerInput
                ref={editRef}
                resetKey={`edit-${message.id}`}
                initialValue={message.body}
                usersById={usersById}
                meId={meId}
                submitOnEnter={false}
                onSubmit={handleSave}
                onCancel={() => setEditing(false)}
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-2 py-1.5">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-md bg-primary-600 px-2 py-1 text-xs font-semibold text-white hover:bg-primary-500"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className={bubbleBase}>
            {message.replyToId ? (
              <button
                type="button"
                onClick={() =>
                  message.replyToId && scrollToMessage(message.replyToId)
                }
                title={repliedTo ? "Jump to original message" : undefined}
                className={classNames(
                  "mb-1.5 block w-full rounded-md border-l-2 px-2 py-1 text-left text-xs",
                  isMine
                    ? "border-white/50 bg-white/10 text-primary-50"
                    : "border-primary-300 bg-gray-50 text-gray-500"
                )}
              >
                {repliedTo ? (
                  <>
                    <span className="block font-semibold">
                      {repliedAuthor
                        ? fullNameFor(repliedAuthor)
                        : "Unknown"}
                    </span>
                    <span className="line-clamp-2 break-words">
                      {repliedTo.body ||
                        (repliedTo.attachments?.length ? "📎 Attachment" : "")}
                    </span>
                  </>
                ) : (
                  <span className="italic">Original message not loaded</span>
                )}
              </button>
            ) : null}
            <ChatMessageBody body={message.body} onPrimary={isMine} />
            {message.attachments && message.attachments.length > 0 ? (
              <ChatMessageAttachments
                channelId={message.channelId}
                attachments={message.attachments}
                onPrimary={isMine}
              />
            ) : null}
            {message.editedAt ? (
              <span
                className={classNames(
                  "ml-1 text-[10px]",
                  isMine ? "text-primary-100/80" : "text-gray-400"
                )}
              >
                (edited)
              </span>
            ) : null}
          </div>
        )}

        {/* Hover-only timestamp for messages that are squished into a
            group (no header above them). Sits under the bubble on the
            right edge for own messages and the left edge for others. */}
        {!showHeader ? (
          <span className="mt-0.5 px-1 text-[10px] text-gray-400 opacity-0 group-hover:opacity-100">
            {formatTime(message.createdAt)}
          </span>
        ) : null}

        {message.reactions.length > 0 ? (
          <div
            className={classNames(
              "mt-1 flex flex-wrap gap-1",
              isMine ? "justify-end" : ""
            )}
          >
            {message.reactions.map((r) => {
              const mine = r.userIds.includes(meId);
              const names = r.userIds
                .map((id) => {
                  const u = usersById.get(id);
                  return u ? u.forename : "Someone";
                })
                .join(", ");
              return (
                <button
                  key={r.emoji}
                  type="button"
                  onClick={() => onReact(message.id, r.emoji)}
                  title={`${names} reacted with ${r.emoji}`}
                  className={classNames(
                    "flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs shadow-sm",
                    mine
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  )}
                >
                  <span>{r.emoji}</span>
                  <span className="font-semibold">{r.userIds.length}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Hover toolbar — sits on the *inside* of the bubble (toward
          the centre of the column) so it never overlaps the author
          gutter for others or the screen edge for own messages.
          Ordering: own messages render [toolbar][bubble], others
          render [avatar][bubble][toolbar]. */}
      <div
        className={classNames(
          "invisible relative flex shrink-0 items-start self-start pt-1 group-hover:visible",
          isMine ? "order-1" : "order-3"
        )}
      >
        <div className="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white px-1 py-0.5 text-gray-500 shadow-sm">
          <button
            type="button"
            onClick={() => setShowEmojiPicker((s) => !s)}
            className="rounded-full p-1 hover:bg-gray-100 hover:text-gray-700"
            title="Add reaction"
          >
            <FaceSmileIcon className="h-4 w-4" />
          </button>
          {onReply ? (
            <button
              type="button"
              onClick={() => onReply(message)}
              className="rounded-full p-1 hover:bg-gray-100 hover:text-gray-700"
              title="Reply"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
            </button>
          ) : null}
          {isMine ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-full p-1 hover:bg-gray-100 hover:text-gray-700"
              title="Edit message"
            >
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {showEmojiPicker ? (
          <div
            className={classNames(
              "absolute top-9 z-20 flex gap-1 rounded-md border border-gray-200 bg-white p-1.5 shadow-lg",
              isMine ? "right-0" : "left-0"
            )}
          >
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onReact(message.id, emoji);
                  setShowEmojiPicker(false);
                }}
                className="rounded px-1.5 py-1 text-base hover:bg-gray-100"
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
